import React from "react";
import { useLang } from "../../i18n/LanguageContext";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

// Maps ecosystem keys -> visual treatment
const visualMap = {
  akademie: { col: "lg:col-span-4 lg:row-span-2", img: "https://images.unsplash.com/photo-1502139214982-d0ad755818d8?auto=format&fit=crop&w=1600&q=85" },
  herzbewegung: { col: "lg:col-span-4", img: "/brand/herzbewegung-logo.png", contain: true, bg: "bg-[radial-gradient(circle_at_60%_40%,rgba(236,72,153,0.18),transparent_60%),linear-gradient(180deg,#13091F_0%,#081328_100%)]" },
  vaterstimme: { col: "lg:col-span-2", img: "https://images.unsplash.com/photo-1604881991720-f91add269bed?auto=format&fit=crop&w=1000&q=85" },
  mutterstimme: { col: "lg:col-span-2", img: "https://images.unsplash.com/photo-1543342384-1f1350e27861?auto=format&fit=crop&w=1000&q=85" },
  maennerstimme: { col: "lg:col-span-2", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=1000&q=85" },
  frauenstimme: { col: "lg:col-span-2", img: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=1000&q=85" },
  kinderstimme: { col: "lg:col-span-2", img: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=1000&q=85" },
  mentor: { col: "lg:col-span-3", img: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=85" },
  corporate: { col: "lg:col-span-3", img: "https://images.unsplash.com/photo-1554415707-6e8cfc93fe23?auto=format&fit=crop&w=1200&q=85" },
  book: { col: "lg:col-span-2", img: "https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=1000&q=85" },
  podcast: { col: "lg:col-span-2", img: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?auto=format&fit=crop&w=1000&q=85" },
  tv: { col: "lg:col-span-2", img: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=1000&q=85" },
  app: { col: "lg:col-span-2", img: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1000&q=85" },
  ai: { col: "lg:col-span-2", img: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=1000&q=85" },
};

const Ecosystem = ({ compact = false }) => {
  const { t } = useLang();
  const items = t("ecosystem.items");
  const coming = t("ecosystem.coming");
  return (
    <section id="ecosystem" className="relative py-32 lg:py-48 bg-[#050B1B]" data-testid="ecosystem-section">
      <div className="container-sat">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <div className="overline mb-6">{t("ecosystem.overline")}</div>
            <h2 className="headline-xl text-4xl md:text-5xl lg:text-7xl">{t("ecosystem.title")}</h2>
          </div>
          <p className="serif italic text-xl text-white/55 md:max-w-md">{t("ecosystem.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-4 auto-rows-[180px] lg:auto-rows-[220px]" data-testid="ecosystem-bento">
          {items.map((it, i) => {
            const v = visualMap[it.k] || { col: "lg:col-span-2", img: "" };
            return (
              <Link
                key={it.k}
                to={`/ecosystem/${it.k}`}
                className={`group relative overflow-hidden rounded-2xl border border-white/5 ${v.col} ${v.bg || ""}`}
                data-testid={`ecosystem-card-${it.k}`}
              >
                {v.img && (
                  <img
                    src={v.img}
                    alt=""
                    className={`absolute inset-0 w-full h-full ${v.contain ? "object-contain p-8 opacity-90 group-hover:opacity-100 group-hover:scale-105" : "object-cover opacity-30 group-hover:opacity-45 group-hover:scale-105"} transition duration-700`}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#081328] via-[#081328]/40 to-transparent"></div>
                <div className="relative z-10 p-6 h-full flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">{it.tag}</span>
                    <ArrowUpRight size={18} className="text-white/40 group-hover:text-[#D4AF37] transition" />
                  </div>
                  <div>
                    <h3 className="serif text-2xl md:text-3xl font-light leading-tight">{it.t}</h3>
                    <p className="text-white/55 text-sm mt-2 max-w-xs">{it.d}</p>
                    {it.k !== "akademie" && it.k !== "herzbewegung" && (
                      <span className="inline-block mt-3 text-[10px] uppercase tracking-widest text-white/40">{coming}</span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Ecosystem;
